import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import {
  listFolders,
  getFolderTree,
  fetchSubtreeMembers,
  createFolder,
} from "../../api/folders.api";
import type { ExplorerItem, FileItem, FolderItem } from "@titancloud/shared";

interface MoveToFolderModalProps {
  open: boolean;
  onClose: () => void;
  selectedItems: ExplorerItem[];
  onMove: (targetFolderId: string | null) => void;
  isPending: boolean;
  errorMessage: string | null;
}

/** Match server-side move normalization: top-most selected folders only. */
function normalizeFolderRoots(folders: FolderItem[]): FolderItem[] {
  const sorted = [...folders].sort((a, b) => a.path.length - b.path.length);
  const roots: FolderItem[] = [];
  for (const f of sorted) {
    const under = roots.some(
      (k) => k.id !== f.id && f.path.startsWith(`${k.path}/`)
    );
    if (!under) roots.push(f);
  }
  return roots;
}

function effectiveMoveItems(
  selectedItems: ExplorerItem[],
  blocked: Set<string>
): ExplorerItem[] {
  const folders = selectedItems.filter((i): i is FolderItem => i.kind === "folder");
  const files = selectedItems.filter((i): i is FileItem => i.kind === "file");
  const roots = normalizeFolderRoots(folders);
  const effFiles = files.filter(
    (file) => !file.folderId || !blocked.has(file.folderId)
  );
  return [...roots, ...effFiles];
}

/** Single folder id that directly contains all effective items, if any. */
function sharedContainerFolderId(items: ExplorerItem[]): string | null {
  if (items.length === 0) return null;
  const parents: (string | null)[] = items.map((item) =>
    item.kind === "file" ? (item.folderId ?? null) : (item.parentId ?? null)
  );
  const first = parents[0]!;
  if (first === null) return null;
  if (!parents.every((p) => p === first)) return null;
  return first;
}

export function MoveToFolderModal({
  open,
  onClose,
  selectedItems,
  onMove,
  isPending,
  errorMessage,
}: MoveToFolderModalProps) {
  const qc = useQueryClient();
  const [pickerFolderId, setPickerFolderId] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [showCreateFolder, setShowCreateFolder] = useState(false);

  const selectedFolderIds = useMemo(
    () => selectedItems.filter((i) => i.kind === "folder").map((f) => f.id),
    [selectedItems]
  );

  const blockedQuery = useQuery({
    queryKey: ["subtree-members", selectedFolderIds],
    queryFn: () => fetchSubtreeMembers(selectedFolderIds),
    enabled: open && selectedFolderIds.length > 0,
  });

  const blocked = useMemo(
    () => new Set(blockedQuery.data ?? []),
    [blockedQuery.data]
  );

  const effectiveItems = useMemo(
    () => effectiveMoveItems(selectedItems, blocked),
    [selectedItems, blocked]
  );

  const targetKey = pickerFolderId ?? null;
  const allAlreadyHere =
    effectiveItems.length > 0 &&
    effectiveItems.every((item) => {
      const loc =
        item.kind === "file"
          ? item.folderId ?? null
          : item.parentId ?? null;
      return loc === targetKey;
    });

  const sharedContainerId = useMemo(
    () => sharedContainerFolderId(effectiveItems),
    [effectiveItems]
  );

  const foldersQuery = useQuery({
    queryKey: ["folders", "move-picker", pickerFolderId],
    queryFn: () => listFolders(pickerFolderId),
    enabled: open,
  });

  const treeQuery = useQuery({
    queryKey: ["folders", "tree", pickerFolderId],
    queryFn: () => getFolderTree(pickerFolderId!),
    enabled: open && pickerFolderId != null,
  });

  const createFolderMutation = useMutation({
    mutationFn: (name: string) => createFolder(name, pickerFolderId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["folders", "move-picker"] });
      setNewFolderName("");
      setShowCreateFolder(false);
    },
  });

  useEffect(() => {
    if (!open) {
      setPickerFolderId(null);
      setNewFolderName("");
      setShowCreateFolder(false);
    }
  }, [open]);

  const breadcrumb = pickerFolderId == null ? [] : (treeQuery.data ?? []);

  const parentIdForUp =
    breadcrumb.length >= 2 ? breadcrumb[breadcrumb.length - 2]!.id : null;

  const destinationIsBlockedSubtree =
    pickerFolderId != null && blocked.has(pickerFolderId);

  const nothingToMove = effectiveItems.length === 0;

  const moveHereDisabled =
    isPending ||
    destinationIsBlockedSubtree ||
    nothingToMove ||
    allAlreadyHere;

  const rawChildren = foldersQuery.data ?? [];
  const visibleFolders = rawChildren.filter((f) => !blocked.has(f.id));

  const handleCreateFolder = () => {
    const name = newFolderName.trim();
    if (!name || createFolderMutation.isPending) return;
    createFolderMutation.mutate(name);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Move to folder"
      maxWidth="max-w-md"
    >
      <div className="space-y-4 px-6 py-4">
        <div className="space-y-2 text-sm text-gray-400">
          <p>
            Choose a destination. Folders inside your selection are hidden — you
            cannot move into them.
          </p>
          {sharedContainerId != null && (
            <p className="rounded-md border border-amber-900/50 bg-amber-950/30 px-3 py-2 text-amber-200/90">
              Selected items live in one folder. Moving to the same folder again
              isn&apos;t allowed — pick another location or create a new folder
              below.
            </p>
          )}
          {nothingToMove && !blockedQuery.isLoading && (
            <p className="rounded-md border border-gray-700 bg-gray-800/50 px-3 py-2 text-gray-300">
              Nothing to move: files inside a selected folder move with that
              folder. Adjust your selection if needed.
            </p>
          )}
          {allAlreadyHere && !nothingToMove && (
            <p className="rounded-md border border-amber-900/50 bg-amber-950/30 px-3 py-2 text-amber-200/90">
              Everything you&apos;re moving is already in this location. Choose a
              different folder or create a new one.
            </p>
          )}
          {destinationIsBlockedSubtree && (
            <p className="rounded-md border border-red-900/40 bg-red-950/20 px-3 py-2 text-red-300/90">
              You can&apos;t move into a folder that is part of the selection.
              Go up or pick another destination.
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1 text-sm text-gray-300">
          <button
            type="button"
            onClick={() => setPickerFolderId(null)}
            className={`rounded px-2 py-1 hover:bg-gray-800 ${
              pickerFolderId == null ? "text-brand-400" : "text-gray-400"
            }`}
          >
            Root
          </button>
          {breadcrumb.map((seg) => (
            <span key={seg.id} className="flex items-center gap-1">
              <span className="text-gray-600">/</span>
              <button
                type="button"
                onClick={() => setPickerFolderId(seg.id)}
                disabled={blocked.has(seg.id)}
                title={
                  blocked.has(seg.id)
                    ? "This folder is inside your selection"
                    : undefined
                }
                className={`max-w-32 truncate rounded px-2 py-1 hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40 ${
                  seg.id === pickerFolderId ? "text-brand-400" : "text-gray-400"
                }`}
              >
                {seg.name}
              </button>
            </span>
          ))}
        </div>

        {pickerFolderId != null && (
          <button
            type="button"
            onClick={() => setPickerFolderId(parentIdForUp)}
            className="text-xs text-gray-500 hover:text-gray-300"
          >
            Up one level
          </button>
        )}

        <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-700 bg-gray-950/50">
          {foldersQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : visibleFolders.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-gray-500">
              No subfolders here
            </p>
          ) : (
            <ul className="divide-y divide-gray-800">
              {rawChildren.map((f) => {
                const isBlocked = blocked.has(f.id);
                const isSelectionHome =
                  !isBlocked && sharedContainerId != null && f.id === sharedContainerId;
                return (
                  <li key={f.id}>
                    {isBlocked ? (
                      <div
                        className="flex cursor-not-allowed items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-500 opacity-50"
                        title="Inside your selection — cannot open"
                      >
                        <svg
                          className="h-4 w-4 flex-shrink-0 text-gray-600"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                        </svg>
                        <span className="min-w-0 truncate">{f.name}</span>
                        <span className="text-xs text-gray-600">(in selection)</span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPickerFolderId(f.id)}
                        className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                          isSelectionHome
                            ? "cursor-pointer bg-amber-950/25 text-amber-100/90 hover:bg-amber-950/40"
                            : "text-gray-200 hover:bg-gray-800/80"
                        }`}
                        title={
                          isSelectionHome
                            ? "Contains your selection — open to pick a subfolder or use Move here after navigating up"
                            : undefined
                        }
                      >
                        <svg
                          className="h-4 w-4 flex-shrink-0 text-yellow-500"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                        </svg>
                        <span className="min-w-0 flex-1 truncate">{f.name}</span>
                        {isSelectionHome && (
                          <span className="flex-shrink-0 text-xs text-amber-400/90">
                            current
                          </span>
                        )}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3">
          {!showCreateFolder ? (
            <button
              type="button"
              onClick={() => setShowCreateFolder(true)}
              disabled={isPending || createFolderMutation.isPending}
              className="text-sm font-medium text-brand-400 hover:text-brand-300 disabled:opacity-50"
            >
              + New folder here
            </button>
          ) : (
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-500">
                New folder in{" "}
                {pickerFolderId == null ? "root" : "current location"}
              </label>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
                placeholder="Folder name"
                autoFocus
                className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
              />
              {createFolderMutation.isError && (
                <p className="text-xs text-red-400">
                  {createFolderMutation.error instanceof Error
                    ? createFolderMutation.error.message
                    : "Could not create folder"}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={handleCreateFolder}
                  loading={createFolderMutation.isPending}
                  disabled={!newFolderName.trim()}
                >
                  Create
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowCreateFolder(false);
                    setNewFolderName("");
                    createFolderMutation.reset();
                  }}
                  disabled={createFolderMutation.isPending}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        {errorMessage && (
          <p className="whitespace-pre-wrap text-sm text-red-400">{errorMessage}</p>
        )}

        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-800 pt-4">
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => onMove(pickerFolderId)}
            disabled={moveHereDisabled}
            loading={isPending}
            title={
              allAlreadyHere
                ? "Already in this folder"
                : nothingToMove
                  ? "Nothing to move"
                  : undefined
            }
          >
            Move here
          </Button>
        </div>
      </div>
    </Modal>
  );
}
